import { motion } from "framer-motion";
import { Reveal } from "@/components/landing/Reveal";
import { scores } from "@/lib/landingData";
import { Flame, Target, TrendingUp, Sun } from "lucide-react";

const ScoreBar = ({ label, value, delay }) => (
  <div>
    <div className="mb-2 flex items-center justify-between">
      <span className="text-sm font-medium text-white">{label}</span>
      <span className="font-display text-sm font-bold text-gradient">{value}</span>
    </div>
    <div className="h-2 w-full overflow-hidden rounded-full bg-white/[0.07]">
      <motion.div
        className="h-full rounded-full bg-gradient-brand"
        initial={{ width: 0 }}
        whileInView={{ width: `${value}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1.1, delay, ease: "easeOut" }}
      />
    </div>
  </div>
);

export const LifeMeasured = () => {
  return (
    <section data-testid="measured" id="measured" className="relative py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gradient">
              Your Life. Measured.
            </p>
            <h2 className="mt-5 font-mega text-4xl leading-[0.95] text-white sm:text-5xl lg:text-6xl">
              What Gets Measured
              <br />
              <span className="text-gradient">Gets Improved</span>
            </h2>
            <p className="mt-6 text-lg text-muted-foreground">
              See your progress across every area of your life &mdash; Health, Wealth,
              Career, Business, Learning, and more &mdash; rolled into one Overall Life
              Score.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              {[
                { icon: Target, label: "Goal Completion" },
                { icon: Flame, label: "Habit Streaks" },
                { icon: TrendingUp, label: "Career Progress" },
                { icon: Sun, label: "Daily Priorities" },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.label}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0C0C0E] px-4 py-3"
                  >
                    <Icon className="h-5 w-5 text-gold" />
                    <span className="text-sm text-white">{s.label}</span>
                  </div>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative">
              <div className="pointer-events-none absolute -inset-6 -z-10 glow-violet blur-3xl opacity-60" />
              <div className="rounded-3xl border border-white/10 bg-[#0A0A0C] p-6 shadow-2xl sm:p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Good morning, Michael</p>
                    <p className="font-display text-lg font-bold uppercase tracking-tight text-white">
                      Your Life Score
                    </p>
                  </div>
                  <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full border-2 border-white/15 bg-white/[0.03]">
                    <span className="font-mega text-3xl text-gradient">89</span>
                  </div>
                </div>

                <div className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/90">
                  You&rsquo;re 63 days ahead of schedule. Keep going.
                </div>

                <div className="mt-6 space-y-4">
                  {scores.map((s, i) => (
                    <ScoreBar key={s.label} label={s.label} value={s.value} delay={i * 0.12} />
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default LifeMeasured;
